import { useState } from "react";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import logo from "@/assets/oz-clean-logo.png";
import heroImg from "@/assets/hero-clean.jpg";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const navLinks = [
  { label: "Services", href: "#services", showClass: "lg:inline-flex", hideClass: "lg:hidden" },
  { label: "How It Works", href: "#how-it-works", showClass: "lg:inline-flex", hideClass: "lg:hidden" },
  { label: "Reviews", href: "#reviews", showClass: "xl:inline-flex", hideClass: "xl:hidden" },
  { label: "Get a Quote", href: "#quote", showClass: "xl:inline-flex", hideClass: "xl:hidden" },
  { label: "Contact", href: "#quote", showClass: "2xl:inline-flex", hideClass: "2xl:hidden" },
];

const HeroSection = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <section className="bg-[#FFD400] relative overflow-hidden">
        {/* Sticky Header */}
        <header className="sticky top-0 z-50 bg-[#FFD400]">
          <div className="container flex flex-nowrap items-center justify-between gap-4 py-3 whitespace-nowrap">
            <div className="flex flex-nowrap items-center gap-3 min-w-0">
              <button
                className="text-[#111111] p-1 md:hidden"
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <a href="/">
                <img src={logo} alt="OZ CLEAN CO. Logo" className="h-14 md:h-20 w-auto shrink-0" width="288" height="192" />
              </a>
              <nav className="hidden md:flex flex-nowrap items-center gap-6 ml-6">
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className={`hidden ${link.showClass} text-[#111111]/80 font-semibold text-sm hover:text-[#111111] transition-colors whitespace-nowrap`}
                  >
                    {link.label}
                  </a>
                ))}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    aria-label="More navigation"
                    className="hidden md:inline-flex 2xl:hidden items-center gap-1 text-[#111111]/80 font-semibold text-sm hover:text-[#111111] transition-colors whitespace-nowrap focus:outline-none"
                  >
                    More <ChevronDown className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#FFD400] border-[#111111]/10">
                    {navLinks.map((link) => (
                      <DropdownMenuItem
                        key={link.label}
                        asChild
                        className={`${link.hideClass} text-[#111111] font-semibold focus:bg-[#111111]/10 focus:text-[#111111]`}
                      >
                        <a href={link.href}>{link.label}</a>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </nav>
            </div>

            <div className="flex flex-nowrap items-center gap-2 md:gap-3 shrink-0">
              <a
                href="tel:1300000000"
                aria-label="Call Now"
                className="inline-flex items-center gap-2 border-2 border-[#111111] text-[#111111] px-3 md:px-4 py-2 font-bold text-sm rounded-full hover:bg-[#111111]/5 transition-colors whitespace-nowrap shrink-0"
              >
                <Phone className="w-4 h-4 shrink-0" />
                <span className="hidden lg:inline">Call Now</span>
              </a>
              <a
                href="#quote"
                className="inline-flex bg-[#0066FF] text-white px-4 md:px-5 py-2 font-bold text-sm rounded-full hover:bg-[#0066FF]/90 transition-colors whitespace-nowrap shrink-0 shadow-[0_2px_8px_rgba(0,102,255,0.25)]"
              >
                Get Free Quote
              </a>
            </div>
          </div>

          {menuOpen && (
            <nav className="md:hidden border-t border-[#111111]/10 bg-[#FFD400]">
              <div className="container py-4 flex flex-col gap-3">
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className="text-[#111111] font-bold text-base py-2 border-b border-[#111111]/10 last:border-b-0"
                  >
                    {link.label}
                  </a>
                ))}
                <a
                  href="tel:1300000000"
                  className="flex items-center gap-2 text-[#111111] font-bold text-base py-2"
                >
                  <Phone className="w-5 h-5" />
                  Call Now
                </a>
              </div>
            </nav>
          )}
        </header>

        {/* Hero Content */}
        <div className="relative z-10 container">
          <div className="flex flex-col xl:flex-row xl:items-end min-h-[420px] md:min-h-[480px] xl:min-h-[520px]">
            <div className="w-full xl:w-1/2 flex flex-col justify-center items-center xl:items-start text-center xl:text-left py-10 md:py-16 xl:py-20 pr-0 xl:pr-12">
              <h1 className="text-5xl md:text-6xl xl:text-6xl font-black text-[#111111] leading-[1.08] tracking-tight">
                Cleaning Made Simple
              </h1>
              <p className="mt-8 text-lg md:text-xl xl:text-lg text-[#111111]/80 max-w-md font-medium leading-relaxed">
                Professional cleaning services for homes, offices and commercial properties.
                No hidden fees. No lock-in contracts. No missed appointments. Just reliable
                cleaners who get the job done right.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center xl:justify-start w-full sm:w-auto">
                <a
                  href="#quote"
                  className="bg-[#0066FF] text-white px-8 py-4 text-lg font-bold rounded-md hover:bg-[#0066FF]/90 transition-colors text-center shadow-[0_4px_14px_rgba(0,102,255,0.3)]"
                >
                  Get Free Quote
                </a>
                <a
                  href="#services"
                  className="border-2 border-[#111111] text-[#111111] px-8 py-4 text-lg font-bold rounded-md hover:bg-[#111111]/5 transition-colors text-center"
                >
                  Our Services
                </a>
              </div>
            </div>
            <div className="w-full xl:w-1/2 relative flex items-end justify-center xl:justify-end mt-4 xl:mt-0">
              <img
                src={heroImg}
                alt="OZ CLEAN CO. mascot kangaroo with cleaning van and equipment"
                width="1280"
                height="896"
                className="w-[90%] md:w-[75%] xl:w-[115%] max-w-none h-auto object-contain object-bottom xl:object-right-bottom xl:-mr-12 2xl:-mr-20"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="relative -mt-1">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block" preserveAspectRatio="none">
          <polygon points="0,0 1440,50 1440,60 0,60" fill="#FFD400" />
        </svg>
      </div>
    </>
  );
};

export default HeroSection;
